import { Link } from 'react-router-dom'
import { FileText, ArrowLeft } from 'lucide-react'

export default function Terms() {
  return (
    <div className="min-h-screen bg-zenna-cream dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-zenna-violet transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back to Zenna</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-zenna flex items-center justify-center">
              <span className="text-white font-bold text-sm">Z</span>
            </div>
            <span className="font-display font-semibold text-slate-800 dark:text-white">Zenna</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 md:p-12">
          {/* Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 bg-zenna-violet/10 rounded-xl">
              <FileText className="w-8 h-8 text-zenna-violet" />
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white">
                Terms of Service
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Last Updated: February 8, 2026
              </p>
            </div>
          </div>

          {/* Terms Content */}
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Welcome to Zenna! These Terms of Service ("Terms") govern your use of the Zenna 
              platform operated by ZenFlow Studios LLC. By using Zenna, you agree to these Terms.
            </p>

            <hr className="my-8 border-slate-200 dark:border-slate-700" />

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                1. Acceptance of Terms
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                By accessing or using Zenna at zennago.com ("Service"), you agree to be bound 
                by these Terms. If you do not agree, please do not use the Service.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                2. Description of Service
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                Zenna is an AI-powered craft design platform that helps you create designs for 
                embroidery, Cricut cutting machines, cross-stitch, and other crafts. The Service 
                includes AI image generation, text tools, pattern creation, and file exports.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                3. Account Registration
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                To use certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300">
                <li>Provide accurate and complete information</li>
                <li>Keep your account credentials secure</li>
                <li>Notify us of any unauthorized access</li>
                <li>Be responsible for all activity under your account</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                4. Subscriptions and Payments
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Billing
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Paid subscriptions are billed in advance on a monthly or annual basis. 
                Credit packs are one-time purchases. All payments are processed by Stripe.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Refunds
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Credit packs are non-refundable. Subscription cancellations receive a prorated 
                refund at the rate of $0.40 per unused credit. Refunds are processed within 
                5-10 business days.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Credits
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Credits never expire. Credits are non-transferable between accounts. 
                Unused credits roll over each month for active subscribers.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                5. Acceptable Use
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                You agree NOT to use Zenna to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300">
                <li>Create illegal, harmful, or offensive content</li>
                <li>Generate content that infringes on others' intellectual property</li>
                <li>Create content depicting minors in inappropriate situations</li>
                <li>Harass, abuse, or harm others</li>
                <li>Attempt to bypass usage limits or security measures</li>
                <li>Use the Service for any unlawful purpose</li>
                <li>Resell or redistribute the Service without permission</li>
              </ul>
              <p className="text-slate-600 dark:text-slate-300 mt-4">
                We reserve the right to suspend or terminate accounts that violate these terms. 
                Suspicious requests may be logged and reviewed.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                6. Intellectual Property
              </h2>
              
              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Your Content
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                You retain ownership of designs you create using Zenna. By using the Service, 
                you grant us a license to store and display your content as necessary to 
                provide the Service.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Our Content
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                The Zenna platform, including its design, code, and AI models, is owned by 
                ZenFlow Studios LLC. You may not copy, modify, or distribute our platform.
              </p>

              <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mt-4 mb-2">
                Commercial Use
              </h3>
              <p className="text-slate-600 dark:text-slate-300">
                Users on Creator tier and above may use their designs for commercial purposes. 
                Free and Hobby tier users may only use designs for personal, non-commercial projects.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                7. AI-Generated Content
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                Zenna uses AI to generate designs. AI-generated content may occasionally be 
                unexpected or imperfect. You are responsible for reviewing and approving all 
                content before use. We do not guarantee that AI outputs will meet your specific 
                requirements.
              </p>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                8. Limitation of Liability
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZENFLOW STUDIOS LLC SHALL NOT BE LIABLE 
                FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SERVICE.
              </p>
              <p className="text-slate-600 dark:text-slate-300 mt-4">
                Our total liability shall not exceed the amount you paid us in the 12 months 
                preceding the claim.
              </p>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                9. Disclaimer of Warranties
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR 
                IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, 
                OR SECURE.
              </p>
            </section>

            {/* Section 10 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                10. Account Suspension
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                We may suspend or terminate your account for violations of these Terms. 
                If your account is suspended:
              </p>
              <ul className="list-disc list-inside space-y-2 text-slate-600 dark:text-slate-300 mt-4">
                <li>Your credits will be frozen pending review</li>
                <li>You may appeal by emailing support@zennago.com</li>
                <li>If your appeal is successful, your account will be restored</li>
                <li>If your appeal is denied, remaining credits may be donated to our "Craft for Good" program</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                11. Changes to Terms
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                We may modify these Terms at any time. We will notify you of significant changes 
                via email or by posting a notice on the Service. Your continued use after changes 
                constitutes acceptance.
              </p>
            </section>

            {/* Section 12 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                12. Governing Law
              </h2>
              <p className="text-slate-600 dark:text-slate-300">
                These Terms are governed by the laws of the State of North Carolina, United States, 
                without regard to conflict of law principles.
              </p>
            </section>

            {/* Section 13 */}
            <section className="mb-8">
              <h2 className="text-xl font-display font-semibold text-slate-900 dark:text-white mb-4">
                13. Contact Us
              </h2>
              <p className="text-slate-600 dark:text-slate-300 mb-4">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-6">
                <p className="font-semibold text-slate-900 dark:text-white mb-2">
                  ZenFlow Studios LLC
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Email:{' '}
                  <a href="mailto:support@zennago.com" className="text-zenna-violet hover:underline">
                    support@zennago.com
                  </a>
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  Website:{' '}
                  <a href="https://zennago.com" className="text-zenna-violet hover:underline">
                    https://zennago.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-6 text-sm text-slate-500 dark:text-slate-400">
            <Link to="/privacy" className="hover:text-zenna-violet transition-colors">
              Privacy Policy
            </Link>
            <span>•</span>
            <Link to="/" className="hover:text-zenna-violet transition-colors">
              Back to Zenna
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}